<template>
  <component
    :is="componentName"
    :to="isRouterLink ? modifiedLink : undefined"
    :href="!isRouterLink && link ? modifiedLink : undefined"
    :target="isExternalLink ? '_blank' : undefined"
    :style="styles"
    :class="classes"
    :qa-locator-id="locators.set()"
    @click="handleCardClick"
  >
    <component :is="titleType" :qa-locator-id="locators.set('name')" class="title" v-html="name" />
    <div
      v-if="description"
      class="description"
      :qa-locator-id="locators.set('description')"
      v-html="description"
    />
    <div v-if="annotation" class="annotation" :qa-locator-id="locators.set('annotation')">
      {{ annotation }}
    </div>
    <Button
      v-if="buttonText"
      class="banner-button"
      :class="{ disabled: buttonDisabled }"
      :name="buttonTextComputed"
      :style="buttonStyle"
      :icon-left="buttonIconLeft"
      :icon-right="buttonIconRight"
      size="l"
      :shadow="true"
      @mouseover.native="onButtonMouseOver"
      @mouseleave.native="onButtonMouseLeave"
      @click="onButtonClick"
    />
    <img v-if="image" class="image" :src="image" :qa-locator-id="locators.set('image')" alt="" />
    <BannersBankLogo v-if="isBankOffer" class="bank-logo" />
    <Loader v-if="isLoading" />
  </component>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import BannersBankLogo from './BannersBankLogo.vue';

import { useQaLocators } from '~/composables/use-qa-locators';
import { useUserTransitions } from '~/composables/use-user-transitions';

import Button from '~/components/controls/Button.vue';
import Loader from '~/components/components/loader/Loader.client.vue';

const COMPONENT_CLASS_NAME: string = 'banners-grid-light-card';

const banner = useBanner();
const user = useUser();
const transitions = useUserTransitions();
const locators = useQaLocators(COMPONENT_CLASS_NAME);

const emit = defineEmits(['button-click', 'card-click']);

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  annotation: {
    type: String,
    default: '',
  },
  link: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '#1CCB01',
  },
  textColor: {
    type: String,
    default: '#fff',
  },
  image: {
    type: String,
    default: '',
  },
  backgroundImage: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  buttonWidth: {
    type: Number,
    default: null,
  },
  buttonText: {
    type: String,
    default: '',
  },
  buttonTextHover: {
    type: String,
    default: '',
  },
  isButtonHoverReact: {
    type: Boolean,
    default: false,
  },
  buttonBackgroundColor: {
    type: String,
    default: '#fff',
  },
  buttonBackgroundColorHover: {
    type: String,
    default: '#1CCB01',
  },
  buttonTextColor: {
    type: String,
    default: '#1CCB01',
  },
  buttonTextColorHover: {
    type: String,
    default: '#fff',
  },
  buttonDisabled: {
    type: Boolean,
    default: false,
  },
  buttonIconLeft: {
    type: String,
    default: '',
  },
  buttonIconRight: {
    type: String,
    default: '',
  },
  titleType: {
    type: String,
    default: 'div',
  },
  isBankOffer: {
    type: Boolean,
    default: false,
  },
  utmContent: {
    type: String,
    default: '',
  },
});

const isButtonOver: Ref<boolean> = ref(false);

const buttonTextComputed = computed((): string => {
  if (!props.buttonTextHover) {
    return props.buttonText;
  }
  return isButtonOver.value ? props.buttonTextHover : props.buttonText;
});

const classes = computed(() => {
  return {
    [COMPONENT_CLASS_NAME]: true,
    'button-hover-react': props.isButtonHoverReact,
  };
});

const styles = computed(() => {
  const reg = /^#([0-9a-f]{3}){1,2}$/i;
  const styles = { color: props.textColor ? props.textColor : '#fff' };
  if (reg.test(props.color)) {
    styles['background-color'] = props.color;
    styles['background-image'] = props.backgroundImage ? backgroundImage() : 'unset';
  } else {
    styles['background-image'] =
      (props.backgroundImage ? `url(${props.backgroundImage}), ` : '') + props.color;
  }
  return styles;
});

const buttonStyle = computed(() => {
  return {
    '--width': props.buttonWidth ? `${props.buttonWidth}px` : 'min-content',
    '--color': props.buttonTextColor,
    '--color-hover': props.buttonTextColorHover,
    '--background-color': props.buttonBackgroundColor,
    '--background-color-hover': props.buttonBackgroundColorHover,
  };
});

const modifiedLink = computed((): string => {
  let link = props.link;

  if (props.utmContent) {
    link = banner.getUrlWithUtmContent(link, props.utmContent);
  }

  if (props.isBankOffer) {
    link = user.getUrlWithUserId(link);
  }

  return link;
});
const isExternalLink = computed((): boolean => {
  return /^(f|ht)tps?:\/\//i.test(props.link);
});

const isRouterLink = computed(
  (): boolean => Boolean(props.link) && !isExternalLink.value && !props.isBankOffer
);

const componentName = computed(() => {
  if (!props.link) {
    return 'div';
  }
  return isRouterLink.value ? defineNuxtLink({}) : 'a';
});

function onButtonMouseOver(): void {
  if (!props.buttonTextHover || !props.isButtonHoverReact) {
    return;
  }
  isButtonOver.value = true;
}

function onButtonMouseLeave(): void {
  if (!props.buttonTextHover || !props.isButtonHoverReact) {
    return;
  }
  isButtonOver.value = false;
}

function backgroundImage(): string {
  if (!props.backgroundImage) {
    return 'unset';
  }

  return props.backgroundImage.startsWith('linear-gradient') ||
    props.backgroundImage.startsWith('radial-gradient')
    ? props.backgroundImage
    : `url(${props.backgroundImage})`;
}

function onButtonClick(event: MouseEvent): void {
  if (props.buttonDisabled) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  if (!props.link) {
    event.preventDefault();
    event.stopPropagation();
    emit('button-click');
  }
}

async function handleCardClick(event: MouseEvent): Promise<void> {
  if (!props.link) {
    emit('card-click');
    return;
  }

  if (props.isBankOffer) {
    event.preventDefault();
    window.open(modifiedLink.value);
    await transitions.createFromUrl(modifiedLink.value);
    return;
  }

  // Обычные внутренние и внешние ссылки обрабатывает сам NuxtLink или браузер.
}
</script>

<style lang="scss" scoped>
.banners-grid-light-card {
  @include radius_xl_l;
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-position: center right;
  background-repeat: no-repeat;
  background-size: cover;
  padding: 32px;
  gap: 16px;

  @include tablet {
    padding: 24px;
  }

  > .title {
    font-weight: 500;
    max-width: 300px;
    font-size: 28px;
    line-height: 36px;
    text-align: left;
    word-wrap: break-word;

    @include desktop_sm {
      max-width: 240px;
    }

    @include tablet {
      max-width: calc(100% - 80px);
      font-size: 20px;
      line-height: 28px;
    }

    @include mobile {
      max-width: calc(100% - 104px);
    }
  }

  > .description {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    flex-grow: 1;
    max-width: 560px;

    @include tablet {
      font-size: 14px;
      line-height: 22px;
      max-width: calc(100% - 180px);
    }

    @include mobile {
      max-width: calc(100% - 120px);
      margin-bottom: 54px;
    }

    &.with-arrow {
      @include desktop_sm {
        max-width: calc(100% - 320px);
      }

      @include mobile {
        max-width: calc(100% - 160px);
      }
    }
  }

  > .annotation {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    opacity: 0.8;

    @include tablet {
      font-size: 12px;
      line-height: 20px;
      margin-bottom: -4px;
    }

    @include mobile {
      position: absolute;
      bottom: 12px;
    }
  }

  > .banner-button {
    border-radius: 16px;
    width: var(--width) !important;
    color: var(--color) !important;
    background-color: var(--background-color) !important;

    @media (hover: hover) {
      &:not(.disabled):hover {
        color: var(--color-hover) !important;
        background-color: var(--background-color-hover) !important;
      }
    }

    @include mobile {
      position: absolute;
      bottom: 16px;
      z-index: 2;
    }
  }

  &:not(.button-hover-react) {
    > .banner-button {
      &.theme-white,
      &.theme-white:hover {
        background: #fff;
        color: #000;
      }

      &.theme-black,
      &.theme-black:hover {
        background: #000;
        color: #fff;
      }
    }
  }

  > .number-wrapper {
    height: 32px;
    width: 32px;
    font-weight: 600;
    border-radius: 50%;
    background-color: #fff;
    display: grid;
    place-content: center;
    margin-bottom: 20px;
  }

  > .arrow-wrapper {
    position: absolute;
    top: 16px;
    right: 16px;
    height: 56px;
    width: 56px;
    border-radius: 50%;
    background-color: #fff;
    color: $color-brand;
    display: grid;
    place-content: center;
    z-index: 2;
    transition: color $transition;
  }

  > .image {
    position: absolute;
    right: -8px;
    bottom: 0;
    transition: transform ease-in-out 0.5s;
    transform: translate3d(0, 10px, 0) scale(0.95);
    z-index: 1;

    @include tablet {
      width: auto;
      transition: unset;
      transform: unset;
      max-width: 280px;
    }

    @include mobile {
      max-width: 240px;
    }

    &.with-arrow {
      right: 80px;

      @include mobile {
        right: -8px;
      }
    }
  }

  > .bank-logo {
    margin-top: 16px;
    z-index: 10;
  }

  @media (hover: hover) {
    &:hover {
      > .image {
        transform: translate3d(0, 0, 0) scale(1);
      }

      > .arrow-wrapper {
        color: $color-brand !important;
      }
    }
  }
}

@include mobile {
  .swiper-slide > .banners-grid-card > .annotation {
    bottom: 25px !important;
  }
}
</style>
